/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is an exchange kind, a status detail or an outcome code; it renders nothing */
// Spec 0.6, the shared HTTP error taxonomy: one exchange, two answers. C1's
// column (did the API answer? a 4xx is a success) and C4's (what a queued op
// becomes). Driven by scheduler_backoff.json's classification cases.
import { OUTCOME, PRECHECK_503_DETAILS, ROUTE_FAMILY_PER_COPY, DETAIL_NOT_FOUND, type OpOutcome } from './offline.codes'

export type HttpExchange =
  /** The request never left (DNS, offline, connect refused before send). */
  | { kind: 'not_sent' }
  /** No response after the request may have been sent (timeout, reset, abort mid-flight). */
  | { kind: 'no_response_after_send' }
  /** An HTTP answer. `detail`: the error body's code; `routeFamily` 'per_copy' for the per-copy dark gate. */
  | { kind: 'answered'; status: number; detail?: string | null; code?: string | null; routeFamily?: string | null; retryAfterS?: number | null }
  /** The database answered with a SQLSTATE / PGRST code and no status. */
  | { kind: 'answered_without_status'; code: string }

/** C1: did the API answer? A 4xx (a 401 included) is a success; 5xx and 503 are failures. */
export function reachability(x: HttpExchange): boolean {
  switch (x.kind) {
    case 'not_sent':
    case 'no_response_after_send':
      return false
    case 'answered_without_status':
      return true
    case 'answered':
      return !(x.status >= 500 && x.status <= 599)
  }
}

/** C1 for a plain status (apiFetch's report). */
export function reachabilityOfStatus(status: number): boolean {
  return reachability({ kind: 'answered', status })
}

/** C4: what the op becomes. */
export function opOutcome(x: HttpExchange): OpOutcome {
  switch (x.kind) {
    case 'not_sent':
      return OUTCOME.transient
    case 'no_response_after_send':
      return OUTCOME.ambiguous
    case 'answered_without_status':
      return OUTCOME.failedPermanent
    case 'answered': {
      if (x.code === 'PGRST301' || x.code === 'PGRST302') return OUTCOME.needsSession
      const s = x.status
      if ((s >= 200 && s < 300) || s === 304) return OUTCOME.done
      if (s === 401) return OUTCOME.needsSession
      if (s === 404) {
        return x.detail === DETAIL_NOT_FOUND && x.routeFamily === ROUTE_FAMILY_PER_COPY
          ? OUTCOME.heldSurfaceUnavailable
          : OUTCOME.failedPermanent
      }
      if (s === 408 || s === 429) return OUTCOME.transient
      if (s === 409) {
        switch (x.detail) {
          case 'copies_unavailable': return OUTCOME.transient
          case 'claim_required': return OUTCOME.heldClaimRequired
          case 'scan_client_key_reused':
          case 'scan_import_unverifiable': return OUTCOME.heldNeedsReview
          default: return OUTCOME.failedPermanent
        }
      }
      if (s === 503) return x.detail && PRECHECK_503_DETAILS.has(x.detail) ? OUTCOME.transient : OUTCOME.ambiguous
      if (s >= 500 && s <= 599) return OUTCOME.ambiguous
      return OUTCOME.failedPermanent
    }
  }
}

/** A thrown fetch: the request may have been sent unless the browser said it is offline. */
export function exchangeForThrow(onLine: boolean): HttpExchange {
  return onLine ? { kind: 'no_response_after_send' } : { kind: 'not_sent' }
}

/** `detail` of an error body: a string, or an object with code / key. */
export function detailOf(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const d = (body as { detail?: unknown }).detail
  if (typeof d === 'string') return d
  if (d && typeof d === 'object') {
    const o = d as { code?: unknown; key?: unknown }
    if (typeof o.code === 'string') return o.code
    if (typeof o.key === 'string') return o.key
  }
  const code = (body as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}

/** Retry-After in seconds (delta-seconds only), or null. */
export function retryAfterSeconds(header: string | null | undefined): number | null {
  if (!header) return null
  const t = header.trim()
  return /^\d+$/.test(t) ? parseInt(t, 10) : null
}
